import {
  Autocomplete,
  Box,
  Chip,
  createFilterOptions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import {
  Control,
  Controller,
  UseFormGetValues,
  UseFormSetValue,
  UseFormTrigger
} from 'react-hook-form';

import {
  CurrencyDropDown,
  DocumentType,
  EntryType,
  Invoice,
  InvoiceExpenseType,
  InvoiceStages,
  PaymentChannel,
  PaymentStatus
} from 'openapi';

import { useTranslations } from 'context/TranslationContext';

import { DocumentConfig } from 'utils/config/documentEntries';
import { DATE_FORMATS } from 'utils/constants/constants';
import {
  DOCUMENT_TYPES_TRANSLATION_KEYS,
  PAYMENT_CHANNELS_TRANSLATION_KEYS,
  PAYMENT_STATUSES_TRANSLATION_KEYS
} from 'utils/constants/invoices';
import { validateDateValue } from 'utils/helpers/dates';
import { processMoneyInput } from 'utils/helpers/moneyHelper';
import { validateDocumentNumber } from 'utils/helpers/validationHelpers';
import { InvoiceDetails } from 'utils/interfaces/InvoiceProps';

import {
  verificationFormFields,
  verificationFormFieldsWrapper,
  verificationFormItem,
  verificationFormItemNoHeight
} from 'styles/pages/InvoiceVerificationStyle';

import { EditCounterpartyField } from './EditCounterpartyField';

interface EditInvoiceDataFormProps {
  currentInvoice: Invoice;
  control: Control<InvoiceDetails, any>;
  expenseTypeOptions: InvoiceExpenseType[];
  currencies: CurrencyDropDown[];
  availableTags: string[];
  isInEditMode?: boolean;
  watch: (
    name: string,
    defaultValue?: string | number | undefined
  ) => string | number;
  setAvailableTags: (value: React.SetStateAction<string[]>) => void;
  documentConfig: DocumentConfig;
  setValue: UseFormSetValue<InvoiceDetails>;
  getValues: UseFormGetValues<InvoiceDetails>;
  trigger: UseFormTrigger<InvoiceDetails>;
}

export const EditInvoiceDataForm = ({
  currentInvoice,
  control,
  expenseTypeOptions,
  currencies,
  availableTags,
  isInEditMode,
  watch,
  setAvailableTags,
  documentConfig,
  setValue,
  getValues,
  trigger
}: EditInvoiceDataFormProps) => {
  const { translate } = useTranslations();

  const [isInvoiceDateOpen, setInvoiceDateOpen] = useState(false);
  const [isDueDateOpen, setDueDateOpen] = useState(false);
  const [tags, setTags] = useState<string[]>(currentInvoice.tags || []);

  const filter = createFilterOptions<string>();

  const watchedVatBase = watch('vatBase');
  const watchedVatAmount = watch('vatAmount');
  const watchedInvoiceAmount = watch('invoiceAmount');
  const watchedInvoiceDate = watch('invoiceDate');

  const BGN_RESTRICTION_DATE = moment('2026-01-01', 'YYYY-MM-DD');

  const isDateRestricted = (invoiceDateValue: string): boolean => {
    if (!invoiceDateValue) return false;
    const date = moment(
      invoiceDateValue,
      DATE_FORMATS.displayedDateFormat,
      true
    );
    return date.isValid() && date.isSameOrAfter(BGN_RESTRICTION_DATE, 'day');
  };

  // Filter currencies based on date restriction - for BGN only
  const filteredCurrencies = currencies.filter((currency: CurrencyDropDown) => {
    if (!isDateRestricted(String(watchedInvoiceDate))) return true;
    return currency.isoCode !== 'BGN';
  });

  const watchedCurrencyId = watch('currencyId');

  const isInvoiceAmountValid = useMemo(() => {
    if (!watchedInvoiceAmount) {
      return false;
    }

    const total =
      (Number(watchedVatBase) || 0) + (Number(watchedVatAmount) || 0);
    return (
      (Number(watchedInvoiceAmount) || 0).toFixed(2) ===
      Number(total).toFixed(2)
    );
  }, [watchedVatBase, watchedVatAmount, watchedInvoiceAmount]);

  const validateInvoiceAmount = (value?: string | number) => {
    const amountToCheck = value ?? getValues('invoiceAmount');
    if (!amountToCheck) {
      return true;
    }

    const total =
      (Number(getValues('vatBase')) || 0) +
      (Number(getValues('vatAmount')) || 0);
    return (Number(amountToCheck) || 0).toFixed(2) === total.toFixed(2)
      ? true
      : 'errors.invoiceAmountEqual';
  };

  // Check if BGN is currently selected
  const isBgnSelected = useMemo(() => {
    if (!watchedCurrencyId) return false;
    const selectedCurrency = currencies.find((c) => c.id === watchedCurrencyId);
    return selectedCurrency?.isoCode === 'BGN';
  }, [watchedCurrencyId, currencies]);

  // Clear currency if BGN is selected on a restricted date
  useEffect(() => {
    if (
      watchedInvoiceDate &&
      isDateRestricted(String(watchedInvoiceDate)) &&
      isBgnSelected
    ) {
      setValue('currencyId', '');
    }
  }, [watchedInvoiceDate, watchedCurrencyId, setValue]);

  useEffect(() => {
    setAvailableTags((previousTags) =>
      Array.from(new Set([...tags, ...previousTags]))
    );
  }, [tags]);

  const isNoDocumentType = useMemo(
    () => currentInvoice.documentType === DocumentType.NO_DOCUMENT,
    [currentInvoice]
  );

  const isNotDraftDocument = useMemo(
    () => currentInvoice.stage !== InvoiceStages.DRAFT,
    [currentInvoice]
  );

  const isDocumentPaid = useMemo(
    () => currentInvoice.paymentStatus === PaymentStatus.NOT_PAID,
    [currentInvoice]
  );

  const isRevenue = useMemo(
    () => currentInvoice.entryType === EntryType.REVENUE,
    [currentInvoice]
  );

  const counterpartyEditField =
    isInEditMode &&
    currentInvoice.filePath &&
    !currentInvoice.isReimbursement ? (
      <Box sx={verificationFormItem}>
        <Controller
          name="counterpartyId"
          control={control}
          render={({ field }) => (
            <EditCounterpartyField
              currentInvoice={currentInvoice}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </Box>
    ) : null;

  return (
    <Box sx={verificationFormFields}>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        {!isNoDocumentType && (
          <Box
            sx={{
              ...verificationFormFieldsWrapper,
              minHeight: '50px',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: { xs: 3, sm: 2 },
              mb: { xs: 1, sm: 0 }
            }}
          >
            <FormControl sx={verificationFormItem}>
              <Controller
                name="invoiceNumber"
                control={control}
                rules={{
                  required: true,
                  validate: (value) => validateDocumentNumber(value)
                }}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    error={!!error}
                    label={`${translate('labels.invoiceNumber')}*`}
                    name="invoiceNumber"
                    placeholder={translate('labels.invoiceNumber')}
                    sx={verificationFormItem}
                    helperText={
                      error
                        ? translate(
                            error.message
                              ? String(error.message)
                              : 'errors.invoiceNumber'
                          )
                        : ''
                    }
                  />
                )}
              />
            </FormControl>

            <Controller
              name="invoiceDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  const date = moment(
                    value,
                    DATE_FORMATS.displayedDateFormat,
                    true
                  );
                  const dateValue = date.isValid() ? date.toDate() : null;
                  const error = validateDateValue(dateValue, false);
                  if (error) return error;
                  // Check if invoice date is greater than due date
                  const dueDateValue = getValues('dueDate');
                  if (dueDateValue && date.isValid()) {
                    const dueDate = moment(
                      dueDateValue,
                      DATE_FORMATS.displayedDateFormat,
                      true
                    );
                    if (dueDate.isValid() && date.isAfter(dueDate, 'day')) {
                      return 'errors.invoiceDateAfterDueDate';
                    }
                  }
                  return true;
                }
              }}
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => {
                return (
                  <FormControl error={!!error} sx={verificationFormItem}>
                    <DatePicker
                      sx={{ width: '100%' }}
                      format={DATE_FORMATS.displayedDateFormat}
                      open={isInvoiceDateOpen}
                      label={`${translate('labels.invoiceDate')}*`}
                      value={
                        value
                          ? moment(value, DATE_FORMATS.displayedDateFormat)
                          : null
                      }
                      onClose={() => setInvoiceDateOpen(false)}
                      onChange={(event) => {
                        onChange(
                          (event as Moment).format(
                            DATE_FORMATS.displayedDateFormat
                          )
                        );
                        void trigger('dueDate');
                      }}
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message
                            ? translate(error?.message)
                            : '',
                          name: 'invoiceDate',
                          onClick: () => setInvoiceDateOpen(true)
                        },
                        openPickerButton: {
                          onClick: () => setInvoiceDateOpen(true)
                        }
                      }}
                    />
                  </FormControl>
                );
              }}
            />

            <Controller
              name="dueDate"
              rules={{
                required: true,
                validate: (value) => {
                  const date = moment(
                    value,
                    DATE_FORMATS.displayedDateFormat,
                    true
                  );
                  const dateValue = date.isValid() ? date.toDate() : null;
                  const error = validateDateValue(dateValue, false);
                  if (error) return error;
                  // Check if due date is before invoice date
                  const invoiceDateValue = getValues('invoiceDate');
                  if (invoiceDateValue && date.isValid()) {
                    const invoiceDate = moment(
                      invoiceDateValue,
                      DATE_FORMATS.displayedDateFormat,
                      true
                    );
                    if (
                      invoiceDate.isValid() &&
                      date.isBefore(invoiceDate, 'day')
                    ) {
                      return 'errors.dueDateBeforeInvoiceDate';
                    }
                  }
                  return true;
                }
              }}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => {
                return (
                  <FormControl error={!!error} sx={verificationFormItem}>
                    <DatePicker
                      sx={{ width: '100%' }}
                      format={DATE_FORMATS.displayedDateFormat}
                      open={isDueDateOpen}
                      label={`${translate('labels.dueDate')}*`}
                      value={
                        value
                          ? moment(value, DATE_FORMATS.displayedDateFormat)
                          : null
                      }
                      onClose={() => setDueDateOpen(false)}
                      onChange={(event) => {
                        onChange(
                          (event as Moment).format(
                            DATE_FORMATS.displayedDateFormat
                          )
                        );
                        void trigger('invoiceDate');
                      }}
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message
                            ? translate(error?.message)
                            : '',
                          name: 'dueDate',
                          onClick: () => setDueDateOpen(true)
                        },
                        openPickerButton: {
                          onClick: () => setDueDateOpen(true)
                        }
                      }}
                    />
                  </FormControl>
                );
              }}
            />
          </Box>
        )}
        <Box
          sx={{
            ...verificationFormFieldsWrapper,
            height: { xs: '200px', sm: '70px' },
            display: isRevenue ? 'none' : 'flex',
            flexDirection: { xs: 'column', sm: 'row' }
          }}
        >
          {!isNoDocumentType && documentConfig.allowExpenseType && (
            <Controller
              name="expenseType"
              control={control}
              rules={{
                required: documentConfig.allowExpenseType
                  ? translate('errors.expenseType')
                  : false
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl error={!!error} sx={verificationFormItem}>
                  <InputLabel>{`${translate(
                    'labels.expenseType'
                  )}*`}</InputLabel>
                  <Select
                    {...field}
                    value={field.value || ''}
                    sx={{ textAlign: 'left' }}
                    label={`${translate('labels.expenseType')}*`}
                    error={!!error}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 350
                        }
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedOption = expenseTypeOptions.find(
                        (expenseType) => expenseType.id === selected
                      ) as InvoiceExpenseType;

                      return (
                        <span
                          style={{
                            color:
                              selectedOption && !selectedOption.isActive
                                ? 'gray'
                                : 'inherit'
                          }}
                        >
                          {!selectedOption?.isActive
                            ? `${selectedOption.name} ${translate(
                                'labels.deactivatedExpenseTypeValue'
                              )}`
                            : selectedOption.name}
                        </span>
                      );
                    }}
                  >
                    {expenseTypeOptions &&
                      expenseTypeOptions.map(
                        (expenseType: InvoiceExpenseType) =>
                          expenseType.isActive && (
                            <MenuItem
                              key={expenseType.id}
                              value={expenseType.id}
                              disabled={!expenseType.isActive}
                            >
                              {expenseType.name}
                            </MenuItem>
                          )
                      )}
                  </Select>
                  <FormHelperText>
                    {error && translate('errors.expenseType')}
                  </FormHelperText>
                </FormControl>
              )}
            />
          )}
          {isNoDocumentType && (
            <Controller
              name="invoiceDate"
              control={control}
              rules={{
                required: true,
                validate: (value) => {
                  const date = moment(
                    value,
                    DATE_FORMATS.displayedDateFormat,
                    true
                  );
                  const dateValue = date.isValid() ? date.toDate() : null;
                  const error = validateDateValue(dateValue, false);
                  if (error) return error;
                  // For NO_DOCUMENT type, there's no due date to check
                  return true;
                }
              }}
              render={({
                field: { onChange, value },
                fieldState: { error }
              }) => {
                return (
                  <FormControl error={!!error} sx={verificationFormItem}>
                    <DatePicker
                      sx={{ width: '100%' }}
                      format={DATE_FORMATS.displayedDateFormat}
                      open={isInvoiceDateOpen}
                      label={`${translate('labels.invoiceDate')}*`}
                      value={
                        value
                          ? moment(value, DATE_FORMATS.displayedDateFormat)
                          : null
                      }
                      onClose={() => setInvoiceDateOpen(false)}
                      onChange={(event) => {
                        onChange(
                          (event as Moment).format(
                            DATE_FORMATS.displayedDateFormat
                          )
                        );
                      }}
                      slotProps={{
                        textField: {
                          error: !!error,
                          helperText: error?.message
                            ? translate(error?.message)
                            : '',
                          name: 'invoiceDate',
                          onClick: () => setInvoiceDateOpen(true)
                        },
                        openPickerButton: {
                          onClick: () => setInvoiceDateOpen(true)
                        }
                      }}
                    />
                  </FormControl>
                );
              }}
            />
          )}
          {isNoDocumentType && (
            <Controller
              name="invoiceAmount"
              control={control}
              rules={{
                required: true
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(event) => {
                    field.onChange(
                      processMoneyInput(event.target.value, String(field.value))
                    );
                  }}
                  label={`${translate('labels.invoiceAmount')}*`}
                  name="invoiceAmount"
                  placeholder={translate('labels.invoiceAmount')}
                  sx={verificationFormItemNoHeight}
                />
              )}
            />
          )}
          {isNoDocumentType && (
            <Controller
              name="currencyId"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <FormControl error={!!error} sx={verificationFormItemNoHeight}>
                  <InputLabel>{`${translate('labels.currency')}*`}</InputLabel>
                  <Select
                    {...field}
                    sx={{ textAlign: 'left' }}
                    label={`${translate('labels.currency')}*`}
                    key={`${watchedInvoiceDate}`}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 350
                        }
                      }
                    }}
                  >
                    {filteredCurrencies &&
                      filteredCurrencies.map((currency: CurrencyDropDown) => (
                        <MenuItem key={currency.id} value={currency.id}>
                          {currency.isoCode}
                        </MenuItem>
                      ))}
                  </Select>
                  <FormHelperText>
                    {error && translate('errors.currency')}
                  </FormHelperText>
                </FormControl>
              )}
            />
          )}
          {isNotDraftDocument && !isNoDocumentType && (
            <Controller
              name="documentType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth sx={verificationFormItem}>
                  <InputLabel>{`${translate(
                    'labels.documentType'
                  )}*`}</InputLabel>
                  <Select
                    {...field}
                    sx={{ textAlign: 'left' }}
                    label={translate('labels.documentType')}
                  >
                    {Object.values(DocumentType)
                      .filter(
                        (document) => document !== DocumentType.NO_DOCUMENT
                      )
                      .map((documentType) => (
                        <MenuItem key={documentType} value={documentType}>
                          {translate(
                            DOCUMENT_TYPES_TRANSLATION_KEYS[documentType]
                          )}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              )}
            />
          )}
        </Box>
        {!isNoDocumentType && (
          <Box>
            <Box
              sx={{
                ...verificationFormFieldsWrapper,
                minHeight: '40px',
                flexDirection: { xs: 'column', sm: 'row' }
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 3, sm: 2 },
                  width: '100%'
                }}
              >
                <Controller
                  name="vatBase"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      onChange={(event) => {
                        field.onChange(
                          processMoneyInput(
                            event.target.value,
                            String(field.value)
                          )
                        );
                        void trigger('invoiceAmount');
                      }}
                      error={!!error}
                      label={`${translate('labels.vatBase')}*`}
                      name="vatBase"
                      placeholder={translate('labels.vatBase')}
                      sx={verificationFormItemNoHeight}
                      helperText={error && translate('errors.vatBase')}
                    />
                  )}
                />
                <Controller
                  name="vatAmount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(event) => {
                        field.onChange(
                          processMoneyInput(
                            event.target.value,
                            String(field.value)
                          )
                        );
                        void trigger('invoiceAmount');
                      }}
                      label={`${translate('labels.vatAmount')}*`}
                      name="vatAmount"
                      placeholder={translate('labels.vatAmount')}
                      sx={verificationFormItemNoHeight}
                    />
                  )}
                />
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 3, sm: 2 },
                  width: '100%'
                }}
              >
                <Controller
                  name="invoiceAmount"
                  control={control}
                  rules={{
                    required: true,
                    validate: (value) => validateInvoiceAmount(value)
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(event) => {
                        field.onChange(
                          processMoneyInput(
                            event.target.value,
                            String(field.value)
                          )
                        );
                      }}
                      label={`${translate('labels.invoiceAmount')}*`}
                      name="invoiceAmount"
                      error={!isInvoiceAmountValid}
                      placeholder={translate('labels.invoiceAmount')}
                      sx={verificationFormItemNoHeight}
                    />
                  )}
                />
                <Controller
                  name="currencyId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field, fieldState: { error } }) => (
                    <FormControl
                      error={!!error}
                      sx={verificationFormItemNoHeight}
                    >
                      <InputLabel>{`${translate(
                        'labels.currency'
                      )}*`}</InputLabel>
                      <Select
                        {...field}
                        sx={{
                          textAlign: 'left'
                        }}
                        key={`${watchedInvoiceDate}`}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 350
                            }
                          }
                        }}
                        label={`${translate('labels.currency')}*`}
                      >
                        {filteredCurrencies &&
                          filteredCurrencies.map(
                            (currency: CurrencyDropDown) => (
                              <MenuItem key={currency.id} value={currency.id}>
                                {currency.isoCode}
                              </MenuItem>
                            )
                          )}
                      </Select>
                      <FormHelperText>
                        {error && translate('errors.currency')}
                      </FormHelperText>
                    </FormControl>
                  )}
                />
              </Box>
            </Box>
            <FormHelperText
              sx={{ minHeight: 10 }}
              error={!isInvoiceAmountValid}
            >
              {!isInvoiceAmountValid && translate('errors.invoiceAmountEqual')}
            </FormHelperText>
          </Box>
        )}
        {!isNoDocumentType && isNotDraftDocument && (
          <Box
            sx={{
              ...verificationFormFieldsWrapper,
              minHeight: '50px',
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            {counterpartyEditField}
            <Controller
              name="paymentStatus"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth sx={verificationFormItem}>
                  <InputLabel>{translate('labels.paymentStatus')}</InputLabel>
                  <Select
                    {...field}
                    sx={{ textAlign: 'left' }}
                    label={translate('labels.paymentStatus')}
                  >
                    {Object.values(PaymentStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {translate(PAYMENT_STATUSES_TRANSLATION_KEYS[status])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
        )}
        {isNoDocumentType && (
          <Box
            sx={{
              ...verificationFormFieldsWrapper,
              height: '70px'
            }}
          >
            <Controller
              name="expenseType"
              control={control}
              rules={{
                required: documentConfig.allowExpenseType
                  ? translate('errors.expenseType')
                  : false
              }}
              render={({ field, fieldState: { error } }) => (
                <FormControl error={!!error} sx={verificationFormItem}>
                  <InputLabel>{`${translate(
                    'labels.expenseType'
                  )}*`}</InputLabel>
                  <Select
                    {...field}
                    value={field.value || ''}
                    sx={{ textAlign: 'left' }}
                    label={`${translate('labels.expenseType')}*`}
                    error={!!error}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 350
                        }
                      }
                    }}
                    renderValue={(selected) => {
                      const selectedOption = expenseTypeOptions.find(
                        (expenseType) => expenseType.id === selected
                      ) as InvoiceExpenseType;

                      return (
                        <span
                          style={{
                            color:
                              selectedOption && !selectedOption.isActive
                                ? 'gray'
                                : 'inherit'
                          }}
                        >
                          {!selectedOption.isActive
                            ? `${selectedOption.name} ${translate(
                                'labels.deactivatedExpenseTypeValue'
                              )}`
                            : selectedOption.name}
                        </span>
                      );
                    }}
                  >
                    {expenseTypeOptions &&
                      expenseTypeOptions.map(
                        (expenseType: InvoiceExpenseType) =>
                          expenseType.isActive && (
                            <MenuItem
                              key={expenseType.id}
                              value={expenseType.id}
                              disabled={!expenseType.isActive}
                            >
                              {expenseType.name}
                            </MenuItem>
                          )
                      )}
                  </Select>
                  <FormHelperText>
                    {error && translate('errors.expenseType')}
                  </FormHelperText>
                </FormControl>
              )}
            />
          </Box>
        )}
        {!isDocumentPaid && !isNoDocumentType && isInEditMode && (
          <Box sx={{ display: 'flex' }}>
            <Controller
              name="paymentChannel"
              control={control}
              render={({ field }) => (
                <FormControl {...field}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    <Typography>
                      {`${translate('labels.paymentChannel')}:`}
                    </Typography>
                    <RadioGroup row {...field}>
                      {Object.values(PaymentChannel).map((channel) => (
                        <FormControlLabel
                          key={channel}
                          value={channel}
                          control={<Radio />}
                          label={translate(
                            PAYMENT_CHANNELS_TRANSLATION_KEYS[channel]
                          )}
                        />
                      ))}
                    </RadioGroup>
                  </Box>
                </FormControl>
              )}
            />
          </Box>
        )}
        {isNoDocumentType && isNotDraftDocument && (
          <Box
            sx={{
              ...verificationFormFieldsWrapper,
              minHeight: '50px',
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            {counterpartyEditField}
            <Controller
              name="paymentStatus"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth sx={verificationFormItem}>
                  <InputLabel>{translate('labels.paymentStatus')}</InputLabel>
                  <Select
                    {...field}
                    sx={{ textAlign: 'left' }}
                    label={translate('labels.paymentStatus')}
                  >
                    {Object.values(PaymentStatus).map((status) => (
                      <MenuItem key={status} value={status}>
                        {translate(PAYMENT_STATUSES_TRANSLATION_KEYS[status])}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>
        )}
        <Box
          sx={{
            ...verificationFormFieldsWrapper,
            minHeight: '50px',
            marginTop: isNoDocumentType && isDocumentPaid ? 2 : 0
          }}
        >
          <Controller
            name="documentDescription"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                minRows={2}
                maxRows={2}
                label={translate('labels.comment')}
                name="documentDescription"
                placeholder={translate('labels.comment')}
                sx={verificationFormItemNoHeight}
              />
            )}
          />
        </Box>
        <Box
          sx={{
            ...verificationFormFieldsWrapper,
            minHeight: '50px',
            mt: 1
          }}
        >
          <Controller
            name="tags"
            control={control}
            render={({ field }) => (
              <Autocomplete
                {...field}
                fullWidth
                freeSolo
                multiple
                disableCloseOnSelect
                options={availableTags}
                value={tags}
                filterOptions={(options, params) => {
                  const filtered = filter(options, params);
                  if (params.inputValue !== '') {
                    const isExisting = options.some(
                      (option) =>
                        params.inputValue.toLowerCase() === option.toLowerCase()
                    );
                    if (!isExisting) {
                      filtered.push(`Add "${params.inputValue}"`);
                    }
                  }

                  return filtered;
                }}
                getOptionLabel={(option) => {
                  if (option.startsWith('Add "')) {
                    return option;
                  }
                  return option;
                }}
                onChange={(event, newValue) => {
                  const lastValue = newValue[newValue.length - 1];
                  if (lastValue && lastValue.startsWith('Add "')) {
                    const newTag = lastValue.slice(5, -1);
                    const updatedTags = [...tags, newTag];
                    setTags(updatedTags);
                    field.onChange(updatedTags);
                  } else {
                    const uniqueTags = Array.from(new Set(newValue));
                    setTags(uniqueTags);
                    field.onChange(uniqueTags);
                  }
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    {option.startsWith('Add "') ? <em>{option}</em> : option}
                  </li>
                )}
                renderValue={(value: string[], getItemProps) =>
                  value.map((option: string, index: number) => (
                    <Chip
                      sx={{ cursor: 'initial' }}
                      variant="outlined"
                      label={option}
                      {...getItemProps({ index })}
                      key={option}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={translate('labels.tags')}
                    placeholder={translate('labels.addATag')}
                  />
                )}
              />
            )}
          />
        </Box>
      </LocalizationProvider>
    </Box>
  );
};
